import { faMinus } from '@fortawesome/pro-light-svg-icons/faMinus';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import chevRight from '@propertypal/shared/src/resources/icons/tabs/chevRight.svg?url';
import { STANDARD_LINKS } from '@propertypal/web-app/src/constants/navigation';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useState } from 'react';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Text } from '../typography';
import {
  CustomMenuButton,
  LinkBox,
  MenuIcon,
  MoreMenu,
  OptionBox,
  SubLinkBox,
  SubmenuOptions,
} from './MorePanel.style';

interface Props {
  open: boolean;
  cfIpCountry: string | null;
}

interface NavLink {
  text: string;
  url?: string;
  Component?: () => JSX.Element[];
  sublinks?: NavLink[];
  testID?: string;
  niOnly?: boolean;
  isNew?: boolean;
}

const MorePanel: FunctionComponent<Props> = (props) => {
  const moreLinks = STANDARD_LINKS(props.cfIpCountry === 'IE');
  const { open } = props;
  const theme = useTheme();
  const router = useRouter();
  const [activeSubLinks, setActiveSubLinks] = useState('');

  const toggleOption = (item: string) => {
    setActiveSubLinks(item === activeSubLinks ? '' : item);
  };

  const generateOptions = (active: boolean, sublinks: NavLink[]) => {
    return (
      <SubmenuOptions role="menu" $show={active}>
        {sublinks.map((sublink) => {
          return (
            sublink.url && (
              <SubLinkBox href={sublink.url} key={sublink.url} prefetch={false} role="menuitem">
                <Text fontSize={16} color={router?.asPath === sublink.url ? theme.primary : theme.textDark}>
                  {sublink.text}
                </Text>

                <img
                  src={chevRight.src}
                  width={chevRight.width}
                  height={chevRight.height}
                  alt="arrow right"
                  loading="lazy"
                />
              </SubLinkBox>
            )
          );
        })}
      </SubmenuOptions>
    );
  };

  const generateSubLinks = (sublinks: NavLink[]) => {
    return sublinks.map((link: NavLink) => {
      return (
        <li key={link.text}>
          {!!link.sublinks && (
            <>
              <CustomMenuButton onClick={() => toggleOption(link.text)} data-testid={link.text}>
                <OptionBox role="menuitem">
                  <Text color={router?.asPath === link.url ? theme.primary : theme.textDark} fontWeight="bold">
                    {link.text}
                  </Text>
                  <MenuIcon>
                    {link.text === activeSubLinks && (
                      <FontAwesomeIcon icon={faMinus} color={theme.textDark} size={26} />
                    )}
                    {link.text !== activeSubLinks && <FontAwesomeIcon icon={faPlus} color={theme.textDark} size={26} />}
                  </MenuIcon>
                </OptionBox>
              </CustomMenuButton>

              {generateOptions(link.text === activeSubLinks, link.sublinks)}
            </>
          )}

          {!!link.url && (
            <LinkBox href={link.url} prefetch={false} role="menuitem" data-testid={link.testID}>
              <Text color={router?.asPath === link.url ? theme.primary : theme.textDark} fontWeight="bold">
                {link.text}
              </Text>

              <img
                src={chevRight.src}
                width={chevRight.width}
                height={chevRight.height}
                alt="arrow right"
                loading="lazy"
              />
            </LinkBox>
          )}
        </li>
      );
    });
  };

  return (
    <MoreMenu
      id="moreMenu"
      data-testid={`moreTab${open ? 'Active' : 'InActive'}`}
      style={{ display: open ? 'block' : 'none' }}
    >
      {generateSubLinks(moreLinks)}
    </MoreMenu>
  );
};

export default MorePanel;
